import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Grid,
  Typography,
  Paper,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Divider,
  Stack
} from '@mui/material';
import {
  CloudUpload,
  Download,
  Image as ImageIcon,
  CheckCircle,
  Error as ErrorIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNotification } from '../context/NotificationContext';
import { httpRequest } from '../api/helper';

// Styled components
const UploadArea = styled(Paper)(({ theme, dragActive }) => ({
  border: `2px dashed ${dragActive ? theme.palette.primary.main : theme.palette.grey[300]}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  backgroundColor: dragActive ? theme.palette.primary.lighter || theme.palette.primary.light + '20' : theme.palette.background.paper,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[50]
  }
}));

const PreviewImage = styled('img')({
  maxWidth: '150px',
  maxHeight: '150px',
  objectFit: 'cover',
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
});

const CircularLogo = styled('img')({
  width: '120px',
  height: '120px',
  objectFit: 'cover',
  borderRadius: '50%',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
});

const DownloadButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  '&:hover': {
    backgroundColor: theme.palette.grey[100]
  }
}));

const LOGO_API_URL = "common/logo-upload/";
const MAX_FILE_SIZE = 1;  // in MB

const API_BASE = 'http://localhost:8000'; // API base URL - adjust according to your Django server

const LogoUploader = () => {
  const { addNotification } = useNotification();
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);

  const handleFiles = async (files) => {
    const file = files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      addNotification('Please select a valid image file', 'error');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE * 1024 * 1024) {
      addNotification(`File size must be less than ${MAX_FILE_SIZE}MB`, 'error');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreviewImage(e.target.result);
    reader.readAsDataURL(file);

    // Upload file
    setIsUploading(true);
    const formData = new FormData();
    formData.append('original_image', file);

    await httpRequest({
      method: 'POST',
      url: LOGO_API_URL,
      data: formData,
      onSuccess: (data) => {
        // const data = await data.json();
        setUploadedImages(prev => [data, ...prev]);
        // addNotification('Image uploaded and logo created successfully!');
        setPreviewImage(null);
      },
      addNotification,
    });

    setIsUploading(false);
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const downloadImage = (imageUrl, filename) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Load existing images on component mount
  React.useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/images/`);
        if (response.ok) {
          const data = await response.json();
          setUploadedImages(data);
        }
      } catch (error) {
        console.error('Failed to fetch images:', error);
      }
    };
    fetchImages();
  }, []);

  return (
    <Container maxWidth="sm" sx={{ py: 1 }}>
      {/* Upload Area */}
      <Card elevation={3} sx={{ mb: 1 }}>
        <CardContent sx={{ p: 2 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleChange}
            style={{ display: 'none' }}
          />
          
          <UploadArea
            dragActive={dragActive}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => !isUploading && fileInputRef.current?.click()}
            elevation={0}
          >
            {isUploading ? (
              <Stack alignItems="center" spacing={2}>
                <CircularProgress size={48} />
                <Typography variant="h6" color="text.secondary">
                  Processing your image...
                </Typography>
              </Stack>
            ) : previewImage ? (
              <Stack alignItems="center" spacing={2}>
                <PreviewImage src={previewImage} alt="Preview" />
                <Typography variant="body2" color="text.secondary">
                  Image ready for upload
                </Typography>
              </Stack>
            ) : (
              <Stack alignItems="center" spacing={2}>
                <CloudUpload sx={{ fontSize: 48, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Drop your image here, or{' '}
                    <Button
                      variant="text"
                      sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                    >
                      browse
                    </Button>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Supports JPG, PNG, GIF up to {MAX_FILE_SIZE}MB
                  </Typography>
                </Box>
              </Stack>
            )}
          </UploadArea>
        </CardContent>
      </Card>

      {/* Results */}
      {uploadedImages.length > 0 && (
        <Card elevation={3}  sx={{ mt: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={3}>
              <ImageIcon color="primary" />
              <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                Your Logos
              </Typography>
            </Stack>
            
            <Grid container spacing={3}>
              {uploadedImages.map((image) => (
                <Grid item xs={12} md={6} key={image.id}>
                  <Card elevation={2} sx={{ height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                          Upload #{image.id}
                        </Typography>
                        <Chip 
                          label={new Date(image.created_at).toLocaleDateString()}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                      
                      <Grid container spacing={2}>
                        {/* Original Image */}
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" align="center" mb={1} sx={{ fontWeight: 'medium' }}>
                            Original
                          </Typography>
                          <Box sx={{ position: 'relative', textAlign: 'center' }}>
                            <CardMedia
                              component="img"
                              src={`${API_BASE}${image.original_image}`}
                              alt="Original"
                              sx={{
                                height: 140,
                                objectFit: 'cover',
                                borderRadius: 1,
                                boxShadow: 1
                              }}
                            />
                          </Box>
                        </Grid>
                        
                        {/* Circular Logo */}
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" align="center" mb={1} sx={{ fontWeight: 'medium' }}>
                            Circular Logo
                          </Typography>
                          <Box sx={{ position: 'relative', textAlign: 'center' }}>
                            {image.circular_logo ? (
                              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                <CircularLogo
                                  src={`${API_BASE}${image.circular_logo}`}
                                  alt="Circular Logo"
                                />
                                <DownloadButton
                                  size="small"
                                  onClick={() => downloadImage(
                                    `${API_BASE}${image.circular_logo}`,
                                    `logo_${image.id}.png`
                                  )}
                                  title="Download Logo"
                                >
                                  <Download fontSize="small" />
                                </DownloadButton>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  width: 120,
                                  height: 120,
                                  borderRadius: '50%',
                                  backgroundColor: 'grey.200',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mx: 'auto'
                                }}
                              >
                                <CircularProgress size={24} />
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default LogoUploader;
